import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, MessageCircle, KeyRound, ShieldAlert } from "lucide-react";
import { api, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, useConfig, can } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { useFocusTarget } from "@/lib/focus";
import { PageTitle, Loader, Empty, Section, Stat, Field } from "@/components/xd/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { NameForm } from "@/components/xd/NameForm";
import { NotificationList } from "@/pages/admin/AdminNotifications";

const ResetPasswordDialog = ({ u }) => {
  const { t } = useLang();
  const act = useAct();
  const [open, setOpen] = useState(false);
  const [temp, setTemp] = useState("");
  const run = async () => { const r = await act(() => api.post(`/admin/users/${u.id}/reset-password`), false); if (r) setTemp(r.data.temp_password); };
  const close = (o) => { setOpen(o); if (!o) setTemp(""); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild><button className="btn-ghost !min-h-[2.25rem] text-xs" data-testid={`user-reset-password-${u.id}`}><KeyRound size={13} />{t("users.reset_password")}</button></DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white" data-testid={`reset-password-dialog-${u.id}`}>
        <DialogHeader><DialogTitle className="text-start">{t("users.reset_password")} · <span className="ltr-num">{u.uid || "—"}</span></DialogTitle></DialogHeader>
        {!temp ? (
          <div className="space-y-4 text-sm text-zinc-300">
            <p className="flex items-start gap-2 text-amber-200"><ShieldAlert size={16} className="mt-0.5 shrink-0" />{t("users.reset_verify")}</p>
            <p>{t("users.reset_explain")}</p>
            <button type="button" className="btn-xd w-full justify-center" onClick={run} data-testid={`reset-password-confirm-${u.id}`}>{t("users.reset_confirm")}</button>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <p className="text-zinc-300">{t("users.reset_done")}</p>
            <p className="block w-full ltr-num text-xl tracking-wider text-center border border-white/15 px-3 py-3 select-all" data-testid="reset-temp-password">{temp}</p>
            <button type="button" className="btn-ghost w-full justify-center" onClick={() => navigator.clipboard?.writeText(temp).then(() => toast.success(t("common.copied")))} data-testid="reset-temp-copy">{t("common.copy")}</button>
            <p className="text-xs text-zinc-500">{t("users.reset_once")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const waLink = (u, t) => `https://wa.me/${u.phone.replace(/\D/g, "")}?text=${encodeURIComponent(t("users.wa_message", { name: u.first_name || u.contact_person || u.name || "" }))}`;

export const useAct = () => {
  const { t, tErr } = useLang();
  return async (fn, ok = true) => {
    try { const r = await fn(); if (ok) toast.success(t("common.saved")); return r; } catch (e) { toast.error(tErr(e)); return null; }
  };
};

export const ActionDialog = ({ trigger, title, label, onSubmit, checkbox, requireText = true, testid }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [check, setCheck] = useState(false);
  const go = async () => { if (await onSubmit(text, check) !== false) { setOpen(false); setText(""); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white" data-testid={testid}>
        <DialogHeader><DialogTitle className="text-start">{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {label && <Field label={label}><Textarea rows={3} className="xd-input" value={text} onChange={(e) => setText(e.target.value)} data-testid={`${testid}-text`} /></Field>}
          {checkbox && <label className="flex items-center gap-3 text-sm"><Checkbox checked={check} onCheckedChange={(c) => setCheck(!!c)} data-testid={`${testid}-check`} />{checkbox}</label>}
          <button className="btn-xd w-full" disabled={label && requireText && text.trim().length < 2} onClick={go} data-testid={`${testid}-confirm`}>{t("common.confirm")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export function AdminOverview() {
  const { t } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/overview").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader />;
  const cards = [["pending_brokers", "/admin/brokers"], ["pending_dealers", "/admin/dealers"], ["pending_vehicles", "/admin/vehicles"], ["pending_photos", "/admin/vehicles"], ["new_inquiries", "/admin/inquiries"], ["live_vehicles", "/admin/vehicles?tab=all"]];
  return (
    <div className="space-y-8">
      <PageTitle eyebrow={`${user.uid} · ${user.is_super ? t("admin.super") : t("admin.admin")}`} title={t("admin.overview")} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.filter(([k]) => d[k] !== undefined).map(([k, to]) => <Link key={k} to={to}><Stat label={t(`overview.${k}`)} value={d[k]} testid={`overview-${k}`} /></Link>)}
      </div>
      <NotificationList compact />
    </div>
  );
}

const NameDialog = ({ u, onDone }) => {
  const { t } = useLang();
  const act = useAct();
  const [open, setOpen] = useState(false);
  const save = async (body) => { if (await act(() => api.put(`/admin/users/${u.id}/name`, body))) { setOpen(false); onDone(); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-ghost !min-h-[2.25rem] text-xs" data-testid={`user-edit-name-${u.id}`}>{t("profile.edit_name")}</button></DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white" data-testid={`edit-name-${u.id}`}>
        <DialogHeader><DialogTitle className="text-start">{t("profile.edit_name")} · <span className="ltr-num">{u.uid || "—"}</span></DialogTitle></DialogHeader>
        <NameForm user={u} onSave={save} testid={`edit-name-form-${u.id}`} />
      </DialogContent>
    </Dialog>
  );
};

export function AdminUsers({ role }) {
  const { t } = useLang();
  const act = useAct();
  const [sp] = useSearchParams();
  const focus = sp.get("focus");
  const [status, setStatus] = useState(sp.get("status") || "pending");
  const [items, setItems] = useState(null);
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    return api.get("/admin/users", { params: { role, status } }).then((r) => { if (id === reqId.current) setItems(r.data.items); }).catch(() => { if (id === reqId.current) setItems([]); });
  };
  useEffect(() => { setItems(null); load(); }, [role, status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!focus || !items) { return; }
    const row = items.find((u) => u.id === focus);
    if (row) { setTimeout(() => document.querySelector(`[data-testid='user-row-${focus}']`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }
    else if (!sp.get("status") || status === sp.get("status")) {
      api.get("/admin/users", { params: { role } }).then((r) => { const u = r.data.items.find((x) => x.id === focus); if (u && u.status !== status) setStatus(u.status); }).catch(() => {});
    }
  }, [focus, items]); // eslint-disable-line react-hooks/exhaustive-deps
  const approve = async (u, _txt, welcome) => {
    const r = await act(() => api.post(`/admin/users/${u.id}/approve`, { send_welcome: welcome }));
    if (r && welcome) toast.message(t(`email.${r.data.email_status}`));
    load();
  };
  const post = (u, path, body) => act(() => api.post(`/admin/users/${u.id}/${path}`, body)).then(load);
  return (
    <div>
      <PageTitle eyebrow={t("admin.users")} title={t(role === "broker" ? "admin.brokers" : "admin.dealers")} />
      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList className="bg-[#0b0b0b] border border-white/10 flex-wrap h-auto">
          {["pending", "approved", "suspended", "rejected"].map((s) => <TabsTrigger key={s} value={s} data-testid={`users-tab-${s}`}>{t(`ustatus.${s}`)}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {status === "pending" && <p className="mb-4 text-sm text-amber-200 border border-amber-500/30 bg-amber-500/[0.06] p-3 flex items-start gap-2" data-testid="users-verify-note"><ShieldAlert size={16} className="mt-0.5 shrink-0" />{t("users.verify_note")}</p>}
      {!items ? <Loader /> : !items.length ? <Empty text={t("common.none")} /> : (
        <div className="xd-panel overflow-x-auto">
          <table className="xd-table">
            <thead><tr><th>{t("common.id")}</th><th>{t(role === "dealer" ? "form.business_name" : "form.name")}</th><th>{t("form.phone")}</th><th>{t("form.email")}</th><th>{t("common.date")}</th><th /></tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className={u.id === focus ? "bg-[var(--xd-red)]/10 outline outline-1 outline-[var(--xd-red)]/60" : ""}>
                  <td className="ltr-num">{u.uid || "—"}</td>
                  <td>{role === "dealer" ? <Link to={`/admin/dealers/${u.id}`} className="hover:text-white underline-offset-4 hover:underline" data-testid={`dealer-profile-link-${u.id}`}>{u.business_name}<span className="block text-xs text-zinc-500">{u.name}</span></Link> : u.name}
                    {(u.suspension_note || u.rejection_reason) && <span className="block text-xs text-red-400">{u.suspension_note || u.rejection_reason}</span>}</td>
                  <td className="ltr-num">{u.phone}
                    {!u.phone_verified_at && <span className="block text-[10px] text-amber-300 whitespace-nowrap" data-testid={`user-phone-unverified-${u.id}`}>{t("users.phone_unverified")}</span>}</td>
                  <td className="ltr-num text-xs">{u.email || "—"}</td>
                  <td className="ltr-num text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
                  <td className="whitespace-nowrap space-x-2 space-x-reverse">
                    {/\d{6}/.test(u.phone || "") && <a href={waLink(u, t)} target="_blank" rel="noopener noreferrer" className="btn-ghost !min-h-[2.25rem] text-xs" data-testid={`user-whatsapp-${u.id}`}><MessageCircle size={13} />{t("users.whatsapp")}</a>}
                    {["approved", "suspended"].includes(u.status) && <ResetPasswordDialog u={u} />}
                    {role === "broker" && <NameDialog u={u} onDone={load} />}
                    {["pending", "rejected"].includes(u.status) && <ActionDialog testid={`approve-${u.id}`} title={t("users.approve")} checkbox={t("users.send_welcome")} onSubmit={(x, c) => approve(u, x, c)} trigger={<button className="btn-xd !min-h-[2.25rem] text-xs" data-testid={`user-approve-${u.id}`}>{t("users.approve")}</button>} />}
                    {u.status === "pending" && <ActionDialog testid={`reject-${u.id}`} title={t("users.reject")} label={t("common.reason")} onSubmit={(x) => post(u, "reject", { reason: x })} trigger={<button className="btn-ghost !min-h-[2.25rem] text-xs" data-testid={`user-reject-${u.id}`}>{t("users.reject")}</button>} />}
                    {u.status === "approved" && <ActionDialog testid={`suspend-${u.id}`} title={t("users.suspend")} label={t("users.internal_note")} onSubmit={(x) => post(u, "suspend", { note: x })} trigger={<button className="btn-ghost !min-h-[2.25rem] text-xs" data-testid={`user-suspend-${u.id}`}>{t("users.suspend")}</button>} />}
                    {u.status === "suspended" && <button className="btn-ghost !min-h-[2.25rem] text-xs" onClick={() => post(u, "reactivate")} data-testid={`user-reactivate-${u.id}`}>{t("users.reactivate")}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PermBoxes = ({ all, value, onChange }) => {
  const { t } = useLang();
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {all.map((p) => (
        <label key={p} className="flex items-center gap-3 text-sm xd-panel px-3 py-2">
          <Checkbox checked={value.includes(p)} onCheckedChange={(c) => onChange(c ? [...value, p] : value.filter((x) => x !== p))} data-testid={`perm-${p}`} />
          <span>{t(`perm.${p}`)}<span className="block text-[10px] text-zinc-600 ltr-num">{p}</span></span>
        </label>
      ))}
    </div>
  );
};

const AdminFormDialog = ({ all, onDone }) => {
  const { t } = useLang();
  const act = useAct();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", password: "", permissions: [] });
  const save = async () => { if (await act(() => api.post("/admin/admins", { ...f, email: f.email || null }))) { setOpen(false); onDone(); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-xd" data-testid="admin-create-button">{t("admins.create")}</button></DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="admin-create-dialog">
        <DialogHeader><DialogTitle className="text-start">{t("admins.create")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {[["name", "form.name", "text"], ["phone", "form.phone", "tel"], ["email", "form.email_optional", "email"], ["password", "admins.initial_password", "password"]].map(([k, l, type]) => (
            <Field key={k} label={t(l)}><Input type={type} dir={k === "name" ? undefined : "ltr"} className="xd-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-form-${k}`} /></Field>
          ))}
          <p className="text-xs text-zinc-500">{t("admins.no_perms_default")}</p>
          <PermBoxes all={all} value={f.permissions} onChange={(p) => setF({ ...f, permissions: p })} />
          <button className="btn-xd w-full" onClick={save} data-testid="admin-form-submit">{t("common.save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdminRow = ({ a, all, reload }) => {
  const { t } = useLang();
  const act = useAct();
  const [perms, setPerms] = useState(a.permissions);
  return (
    <Section testid={`admin-row-${a.uid}`} title={<span>{a.name} <span className="text-zinc-500 text-sm ltr-num">{a.uid} · {a.phone}</span></span>}
      actions={a.is_super ? <span className="text-xs text-[var(--xd-red)]">{t("admin.super")}</span> : (
        <div className="flex gap-2">
          <button className="btn-xd !min-h-[2.25rem] text-xs" onClick={() => act(() => api.put(`/admin/admins/${a.id}/permissions`, { permissions: perms }))} data-testid={`admin-save-perms-${a.uid}`}>{t("admins.save_perms")}</button>
          <ActionDialog testid={`admin-remove-${a.uid}`} title={t("admins.remove_confirm")} onSubmit={async () => { await act(() => api.delete(`/admin/admins/${a.id}`)); reload(); }}
            trigger={<button className="btn-ghost !min-h-[2.25rem] text-xs" data-testid={`admin-remove-button-${a.uid}`}><Trash2 size={14} /></button>} />
        </div>
      )}>
      {a.is_super ? <p className="text-sm text-zinc-500">{t("admins.super_note")}</p> : <PermBoxes all={all} value={perms} onChange={setPerms} />}
    </Section>
  );
};

const BackupPanel = () => {
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/admin/backup/photos/info").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  return (
    <Section title={t("backup.title")} testid="backup-panel">
      <p className="text-xs text-zinc-500">{t("backup.hint")}</p>
      <div className="flex flex-wrap gap-3">
        <a className="btn-xd" href={`${API}/admin/backup/database`} data-testid="backup-database-button">{t("backup.db")}</a>
        {info && Array.from({ length: info.parts }, (_, i) => (
          <a key={i} className="btn-ghost" href={`${API}/admin/backup/photos?part=${i + 1}`} data-testid={`backup-photos-part-${i + 1}`}>{t("backup.photos")} {info.parts > 1 ? `${i + 1}/${info.parts}` : ""}</a>
        ))}
      </div>
    </Section>
  );
};

export function AdminAdmins() {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/admins").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader />;
  return (
    <div className="space-y-4">
      <PageTitle eyebrow={t("admin.super")} title={t("admin.admins")}><AdminFormDialog all={d.all_permissions} onDone={load} /></PageTitle>
      {d.items.map((a) => <AdminRow key={a.id} a={a} all={d.all_permissions} reload={load} />)}
      <BackupPanel />
    </div>
  );
}

export function AdminAudit() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => api.get("/admin/audit", { params: q ? { action: q } : {} }).then((r) => setItems(r.data.items)), 250);
    return () => { clearTimeout(id); };
  }, [q]);
  return (
    <div>
      <PageTitle eyebrow={t("admin.security")} title={t("admin.audit")}>
        <Input dir="ltr" placeholder="vehicle. / broker. / admin." className="xd-input w-full sm:w-60" value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-filter-input" />
      </PageTitle>
      {!items ? <Loader /> : !items.length ? <Empty text={t("common.none")} /> : (
        <div className="xd-panel overflow-x-auto">
          <table className="xd-table"><thead><tr><th>{t("common.date")}</th><th>{t("audit.actor")}</th><th>{t("audit.action")}</th><th>{t("audit.details")}</th></tr></thead>
            <tbody>{items.map((a) => (
              <tr key={a.id}><td className="ltr-num text-xs whitespace-nowrap">{fmtDate(a.created_at)}</td><td className="ltr-num text-xs">{a.actor_uid || "—"}</td>
                <td className="ltr-num text-xs">{a.action}</td><td className="ltr-num text-xs text-zinc-400 max-w-md break-all">{JSON.stringify(a.details)}</td></tr>
            ))}</tbody></table>
        </div>
      )}
    </div>
  );
}

const CONTENT_KEYS = ["callback_consent"];

const AssistantGuideEditor = () => {
  const { t } = useLang();
  const act = useAct();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [gl, setGl] = useState("he");
  const [role, setRole] = useState(params.get("role") === "dealer" ? "dealer" : "broker");
  useEffect(() => { api.get("/admin/assistant-guide", { params: { lang: gl, role } }).then((r) => { setD(r.data); setText(r.data.text); }).catch(() => {}); }, [gl, role]);
  if (!d) return null;
  const save = async (val) => { const r = await act(() => api.put("/admin/assistant-guide", { text: val, lang: gl, role })); if (r) { setD(r.data); setText(r.data.text); } };
  return (
    <Section title={t(role === "dealer" ? "assist.admin_title_dealer" : "assist.admin_title")} testid="content-assistant-guide">
      <div className="flex gap-2" data-testid="assistant-guide-role">
        {["broker", "dealer"].map((k) => (
          <button key={k} className={role === k ? "btn-xd" : "btn-ghost"} onClick={() => setRole(k)} data-testid={`assistant-guide-role-${k}`}>{t(`assist.role_${k}`)}</button>
        ))}
      </div>
      <p className="text-xs text-zinc-500">{t("assist.admin_hint")}</p>
      {d.updated_at && <p className="text-xs text-zinc-500 ltr-num">{fmtDate(d.updated_at)} · {d.updated_by}</p>}
      <div className="flex gap-2" data-testid="assistant-guide-lang">
        {[["he", "עברית"], ["ar", "العربية"]].map(([k, label]) => (
          <button key={k} className={gl === k ? "btn-xd" : "btn-ghost"} onClick={() => setGl(k)} data-testid={`assistant-guide-lang-${k}`}>{label}</button>
        ))}
      </div>
      <Textarea dir="auto" rows={16} className="xd-input font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} data-testid="assistant-guide-textarea" />
      <div className="flex flex-wrap gap-3">
        <button className="btn-xd" onClick={() => save(text)} data-testid="assistant-guide-save">{t("common.save")}</button>
        <ActionDialog testid="assistant-guide-reset" title={t("assist.reset_confirm")} onSubmit={() => save("")} trigger={<button className="btn-ghost" data-testid="assistant-guide-reset-button">{t("assist.reset")}</button>} />
      </div>
    </Section>
  );
};

export function AdminContent() {
  const { t } = useLang();
  const act = useAct();
  const cfg = useConfig();
  const [items, setItems] = useState(cfg.content);
  const [cat, setCat] = useState({ slug: "", name_he: "", name_ar: "", order: 10 });
  useEffect(() => { setItems(cfg.content); }, [cfg.content]);
  useFocusTarget(true);
  const set = (k, l, v) => setItems({ ...items, [k]: { ...items[k], [l]: v } });
  const translate = async (k, source) => {
    const r = await act(() => api.post("/admin/translate", { source, fields: { text: items[k]?.[source] } }), false);
    if (r) set(k, r.data.target, r.data.fields.text);
  };
  return (
    <div className="space-y-6">
      <PageTitle eyebrow={t("admin.content")} title={t("content.title")}>
        <button className="btn-xd" onClick={async () => { await act(() => api.put("/admin/content", { items })); cfg.reload(); }} data-testid="content-save-button">{t("common.save")}</button>
      </PageTitle>
      <div className="xd-panel p-4 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="content-pages-moved">
        <span className="text-zinc-300">{t("content.moved")}</span>
        <Link to="/admin/site?section=pages" className="btn-ghost" data-testid="content-open-pages">{t("content.open_pages")}</Link>
      </div>
      {CONTENT_KEYS.map((k) => (
        <Section key={k} title={t(`content.${k}`)} testid={`content-${k}`}>
          {k === "callback_consent" && <p className="text-xs text-amber-400" data-testid="content-legal-pending">{t("content.legal_pending")}</p>}
          <div className="grid md:grid-cols-2 gap-4">
            {["he", "ar"].map((l) => (
              <div key={l} className="space-y-2">
                <Textarea rows={k.startsWith("hero") || k === "callback_consent" ? 2 : 6} className="xd-input" value={items[k]?.[l] || ""} onChange={(e) => set(k, l, e.target.value)} data-testid={`content-${k}-${l}`} />
                <button className="text-xs text-zinc-400 hover:text-white underline" onClick={() => translate(k, l)} data-testid={`content-translate-${k}-${l}`}>{t(l === "he" ? "translate.he_ar" : "translate.ar_he")}</button>
              </div>
            ))}
          </div>
        </Section>
      ))}
      <AssistantGuideEditor />
      <Section title={t("content.categories")} testid="categories-section">
        {cfg.categories.map((c) => (
          <div key={c.id} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm">
            <span>{c.name_he} · {c.name_ar} <span className="text-zinc-600 ltr-num">{c.slug}</span></span>
            <button className="text-zinc-500 hover:text-red-400" onClick={async () => { await act(() => api.delete(`/admin/categories/${c.id}`)); cfg.reload(); }} data-testid={`category-delete-${c.slug}`}><Trash2 size={15} /></button>
          </div>
        ))}
        <div className="grid sm:grid-cols-4 gap-3">
          <Input dir="ltr" placeholder="slug" className="xd-input" value={cat.slug} onChange={(e) => setCat({ ...cat, slug: e.target.value })} data-testid="category-slug-input" />
          <Input placeholder="עברית" className="xd-input" value={cat.name_he} onChange={(e) => setCat({ ...cat, name_he: e.target.value })} data-testid="category-he-input" />
          <Input placeholder="العربية" className="xd-input" value={cat.name_ar} onChange={(e) => setCat({ ...cat, name_ar: e.target.value })} data-testid="category-ar-input" />
          <button className="btn-ghost" onClick={async () => { if (await act(() => api.post("/admin/categories", { ...cat, order: Number(cat.order) }))) { setCat({ slug: "", name_he: "", name_ar: "", order: 10 }); cfg.reload(); } }} data-testid="category-add-button">{t("content.add_category")}</button>
        </div>
      </Section>
    </div>
  );
}
